import { User, ChevronDown, Star } from 'lucide-react';

import ipsenAcademyLogo from '@/assets/images/ipsen-academy-logo.png';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { ROUTES } from '@/config/routes';

import defaultConfig from '../config/header-config.json';
import { useAppHeader } from '../hooks/useAppHeader';
import { AppHeaderProps, AppHeaderConfig } from '../types';

export const AppHeader = ({ userProfile, config: configOverride }: AppHeaderProps) => {
  // Merge default config with overrides
  const config = {
    ...defaultConfig,
    ...configOverride,
    display: { ...defaultConfig.display, ...configOverride?.display },
    logo: { ...defaultConfig.logo, ...configOverride?.logo },
    userMenu: { ...defaultConfig.userMenu, ...configOverride?.userMenu },
    breadcrumb: { ...defaultConfig.breadcrumb, ...configOverride?.breadcrumb },
    styles: { ...defaultConfig.styles, ...configOverride?.styles },
  } satisfies AppHeaderConfig;

  const { breadcrumbs, displayName, initials, handleSignOut, navigateHome, navigate } =
    useAppHeader(userProfile, config);

  const handleRedirectToStats = () => {
    // NOTE: hardcoded on demand by Anthony Beth (just so that it would work right now)
    navigate(ROUTES.STATS.replace(':caseId', '0024_IPSEN_spasticiteit'));
  };

  return (
    <header
      className={`bg-${config.styles.backgroundColor} border-b border-${config.styles.borderColor} flex items-center justify-between px-6 py-3`}
    >
      {/* Logo et Breadcrumb */}
      <div className="flex min-w-0 flex-1 items-center gap-6">
        {config.display.showLogo && (
          <ImageWithLoader
            src={ipsenAcademyLogo}
            alt="Ipsen Academy"
            className="h-8 w-auto flex-shrink-0 cursor-pointer"
            containerClassName="flex-shrink-0"
            skeletonClassName="h-8 w-24"
            loading="eager"
            onClick={() => window.open('https://ipsen-academy.com/nl', '_blank')}
          />
        )}

        {config.display.showBreadcrumb && (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.path} className="flex items-center">
                  {index > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem>
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage className={`text-${config.styles.textColor} font-semibold`}>
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => navigate(crumb.path)}
                        className="cursor-pointer text-livelinx-grey200 hover:text-livelinx-text"
                      >
                        {crumb.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </div>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}
      </div>

      {/* Menu utilisateur */}
      {config.display.showUserMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`flex items-center gap-2 hover:bg-${config.styles.hoverColor} h-10`}
            >
              {config.userMenu.showAvatar && (
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt={displayName} />
                  <AvatarFallback className="bg-livelinx-blue300 text-xs font-semibold text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              )}
              {config.userMenu.showName && (
                <span
                  className={`text-${config.styles.textColor} hidden truncate text-sm font-semibold md:inline`}
                  style={{ maxWidth: `${config.userMenu.maxNameLength}px` }}
                >
                  {displayName}
                </span>
              )}
              {config.userMenu.showDropdownIcon && (
                <ChevronDown className="h-4 w-4 text-livelinx-grey200" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={`w-48 bg-${config.styles.dropdownBackground} ${config.styles.dropdownShadow} border-${config.styles.borderColor} z-dropdown`}
          >
            <DropdownMenuItem
              onClick={handleRedirectToStats}
              className={`text-${config.styles.textColor} hover:bg-${config.styles.hoverColor} cursor-pointer hover:text-livelinx-blue300`}
            >
              <Star className="mr-2 h-4 w-4" />
              <span>{config.userMenu.statsLabel}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleSignOut()}
              className={`text-${config.styles.textColor} hover:bg-${config.styles.hoverColor} cursor-pointer hover:text-livelinx-blue300`}
            >
              <User className="mr-2 h-4 w-4" />
              <span>{config.userMenu.logoutLabel}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
};
